/**
 * Google Identity Services: the "Sign in with Google" button (DR-0053). The
 * script loads from accounts.google.com only when the Account panel opens, so
 * nothing rides the boot route (iOS survival rules §8). One nonce per page:
 * Google puts its SHA-256 in every ID token and Supabase checks it against the
 * raw value, so a token lifted from elsewhere can't be replayed here.
 */
import { ACT_GOOGLE_CLIENT_ID } from "@act/shared";

type GisCredentialResponse = { credential?: string };
type GisId = {
  initialize(config: Record<string, unknown>): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
};

declare global {
  interface Window {
    google?: { accounts?: { id?: GisId } };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";

let loading: Promise<GisId> | null = null;
let nonce: Promise<{ raw: string; hashed: string }> | null = null;
let handler: ((idToken: string, rawNonce: string) => void) | null = null;
let initialized = false;

function loadGis(): Promise<GisId> {
  loading ??= new Promise<GisId>((resolve, reject) => {
    const ready = () => window.google?.accounts?.id;
    const existing = ready();
    if (existing) {
      resolve(existing);
      return;
    }
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => {
      const id = ready();
      if (id) resolve(id);
      else reject(new Error("google identity missing"));
    };
    script.onerror = () => reject(new Error("google identity blocked"));
    document.head.appendChild(script);
  }).catch((e: unknown) => {
    loading = null; // let a later open try again
    throw e;
  });
  return loading;
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** The page's nonce: `raw` goes to Supabase, `hashed` (SHA-256 hex) to Google. */
function pageNonce(): Promise<{ raw: string; hashed: string }> {
  nonce ??= (async () => {
    const raw = hex(crypto.getRandomValues(new Uint8Array(32)));
    const hashed = hex(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw))));
    return { raw, hashed };
  })();
  return nonce;
}

/**
 * Render Google's button into `host`. Each sign-in calls `onCredential` with the
 * Google ID token and the raw nonce. Rejects when Google's script can't load
 * (blocked, offline, or an in-app browser).
 */
export async function renderGoogleButton(host: HTMLElement, onCredential: (idToken: string, rawNonce: string) => void): Promise<void> {
  const [gis, n] = await Promise.all([loadGis(), pageNonce()]);
  handler = onCredential;
  if (!initialized) {
    gis.initialize({
      client_id: ACT_GOOGLE_CLIENT_ID,
      nonce: n.hashed,
      callback: (response: GisCredentialResponse) => {
        if (response.credential) handler?.(response.credential, n.raw);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      ux_mode: "popup",
      itp_support: true,
      context: "signin",
    });
    initialized = true;
  }
  gis.renderButton(host, { type: "standard", theme: "filled_black", size: "large", text: "signin_with", shape: "pill", width: 240 });
}
